<template>
	<section class="ballot-container shadow-sm my-4" v-if="ballot" ref="startForm"
			 :style="{'scroll-margin-top':headerHeight}">
		<template v-if="submitted">
			<h2 class="ballot-header">Thank you for voting!</h2>
			<p class="ballot-description">Your response has been recorded successfully.</p>

			<div
				v-for="question in ballot.questions"
				:key="'submitted_' + question.id"
				class="question-card"
			>
				<h4 class="question-title text-primary font-bold">{{ question.title }}</h4>
				<p v-if="question.text" class="question-text text-sm text-gray-500" v-html="question.text"></p>

				<div class="submitted-answers mt-2">
					<ul v-if="question.type === 'checkbox'">
						<template v-if="Array.isArray(submittedAnswers[question.id]) && submittedAnswers[question.id].length">
							<li
								v-for="id in submittedAnswers[question.id]"
								:key="id"
							>
								{{ question.answers.find(a => a.id === id)?.title || 'Unknown option' }}
							</li>
						</template>
						<template v-else>
							<li>No selection</li>
						</template>
					</ul>

					<ul v-else-if="question.type === 'radio'">
						<li>
							{{
								question.answers.find(
									a => a.id === submittedAnswers[question.id]
								)?.title || 'No selection'
							}}
						</li>
					</ul>
				</div>
			</div>
		</template>

		<template v-else-if="ballot">
			<h2 class="ballot-header">{{ ballot.name }}</h2>
			<p class="ballot-description" v-html="ballot.description"></p>

			<p class="ballot-description">
				I, {{ name }}, as a member of the {{ companyName }}, hereby vote as follows:
			</p>

			<div
				v-for="question in ballot.questions"
				:key="question.id"
				class="question-card"
			>
				<h4 class="question-title">{{ question.title }}
					<span v-if="question.is_required" class="text-danger ml-1" title="Required">*</span>
				</h4>
				<div class="error-messages">{{ messageFieldError('answers.' + question.id) }}</div>
				<p v-if="question.text" class="question-text" v-html="question.text"></p>

				<div v-if="question.type === 'radio'">
					<label
						v-for="answer in question.answers"
						:key="answer.id"
						class="answer-option"
					>
						<input
							type="radio"
							:name="'question_' + question.id"
							:value="answer.id"
							v-model="form[question.id]"
						/>
						{{ answer.title }}
					</label>
				</div>

				<div v-else-if="question.type === 'checkbox'" style="display: flex; flex-direction: column;">
					<label
						v-for="answer in question.answers"
						:key="answer.id"
						class="checkbox-wrapper"
					>
						<input
							type="checkbox"
							:value="answer.id"
							:name="'question_' + question.id + '[]'"
							v-model="form[question.id]"
							:disabled="isCheckboxDisabled(question.id, answer.id)"
							@change="handleCheckboxChange(question)"
						/>
						<span>{{ answer.title }}</span>
					</label>

					<p
						v-if="question.max_selections > 0"
						class="text-sm text-gray-500 mt-1"
					>
						Select at least {{ question.min_selections }}
						{{ question.min_selections === 1 ? 'option' : 'options' }},
						up to {{ question.max_selections }} {{ question.max_selections === 1 ? 'option' : 'options' }}.
					</p>
				</div>
			</div>

			<div>
				Please review your selection carefully before pressing the button below to submit your vote.
			</div>

			<button class="btn-primary custom-button btn mt-2" @click="submit">
				Register Vote Selection
			</button>
		</template>
	</section>
</template>

<script>
import ConfirmDialog from "../dialogs/ConfirmDialog.vue";
import notifications from "../mixins/notifications";
import {headerOffset} from "../mixins/headerOffset";

export default {
	name: "BallotVoteComponent",
	components: {ConfirmDialog},
	mixins: [notifications, headerOffset],
	props: {
		ballot: {
			type: Object,
			required: false,
			default: null,
		},
		companyName: {
			type: String,
			default: null
		}
	},
	data: () => ({
		isLoading: false,
		submitted: false,
		submittedAnswers: {},
		name: '',
		email: '',
		form: {}
	}),
	computed: {},
	mounted() {
		this.initForm();
		this.loadAuth();
	},
	watch: {},
	methods: {
		initForm() {
			if (this.ballot?.questions?.length) {
				this.ballot.questions.forEach(question => {
					if (question.type === 'checkbox') {
						this.$set(this.form, question.id, []);
					}
				});
			}
		},
		loadAuth() {
			this.isLoading = true;

			this.$api.get('api/v1/auth').then(response => {
				if (response.data && response.data.is_auth) {
					const data = response.data.user;
					this.name = data.full_name;
					this.email = data.email;
				}
			}).finally(() => {
				this.isLoading = false;
			});
		},
		handleCheckboxChange(question) {
			if (!Array.isArray(this.form[question.id])) {
				this.$set(this.form, question.id, []);
			}
		},
		isCheckboxDisabled(questionId, answerId) {
			const question = this.ballot.questions.find(q => q.id === questionId);
			const selected = this.form[questionId] || [];
			const isSelected = selected.includes(answerId);

			return (
				question.max_selections > 0 &&
				Array.isArray(selected) &&
				selected.length >= question.max_selections &&
				!isSelected
			);
		},
		submit() {
			this.isLoading = true;

			const payload = {
				ballot_id: this.ballot.id,
				answers: this.form,
			};

			this.$api.post(`api/v1/ballot-vote/vote`, payload)
				.then(response => {
					if (response.data) {
						this.submittedAnswers = JSON.parse(JSON.stringify(this.form));
						this.clearValidateErrors();
						this.submitted = true;
					}
				}).catch((error) => {
				if (error.response && error.response.data.errors) {
					this.setValidateErrors(error.response.data.errors);
				}
			}).finally(() => {
				this.isLoading = false;
				this.$emit('reload');
			});

			this.$refs.startForm.scrollIntoView({behavior: 'smooth'});
		}
	}
}
</script>

<style scoped>
.ballot-container {
	background-color: #f9fafb;
	padding: 2rem;
	margin: 0 auto;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.ballot-header {
	font-size: 1.8rem;
	font-weight: 600;
	margin-bottom: 1rem;
	color: #1f2937;
}

.ballot-description {
	color: #4b5563;
	margin-bottom: 2rem;
}

.question-card {
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	padding: 1.5rem;
	margin-bottom: 1.5rem;
}

.question-title {
	font-size: 1.2rem;
	font-weight: 600;
	margin-bottom: 0.5rem;
	color: #111827;
}

.question-text {
	color: #6b7280;
	font-size: 0.9rem;
	margin-bottom: 1rem;
}

.answer-option {
	display: flex;
	align-items: center;
	margin-bottom: 0.5rem;
}

.answer-option input {
	margin-right: 0.5rem;
}

.custom-button {
	color: white;
	border: none;
	border-radius: 6px;
	font-weight: 600;
	cursor: pointer;
	transition: background-color 0.2s ease;
}

.submit-button:hover {
	background-color: #1d4ed8;
}

.error-messages {
	color: red;
	font-size: 0.8em;
}
</style>
